import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button, Card, StatusPill } from '@careandkey/shared';
import { supabase } from '../lib/supabase';
import { Layout, Topbar } from '../components/Layout';
import { PropertyForm, type PropertyRow } from '../components/EntityForms';

type Row = { id: string; name: string; property_type: string; address: string | null; client_id: string | null; area_m2: number | null; access_notes: string | null; clients: { name: string } | null };

export function PropertiesList() {
  const { t } = useTranslation();
  const nav = useNavigate();
  const [rows, setRows] = useState<Row[]>([]);
  const [loading, setLoading] = useState(true);
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<PropertyRow | null>(null);

  const load = useCallback(async () => {
    const r = await supabase.from('properties').select('id,name,property_type,address,client_id,area_m2,access_notes,clients(name)').is('deleted_at', null).order('name');
    setRows((r.data as unknown as Row[]) ?? []);
    setLoading(false);
  }, []);
  useEffect(() => { load(); }, [load]);

  const openNew = () => { setEditing(null); setFormOpen(true); };
  const openEdit = (r: Row) => { setEditing(r); setFormOpen(true); };

  return (
    <Layout header={<Topbar actions={<Button onClick={openNew}>+ {t('form.newProperty')}</Button>} />}>
      <div className="px-7 pt-6">
        <h1 className="text-[30px] font-semibold tracking-[-0.02em]">{t('nav.properties')}</h1>
      </div>
      <div className="px-7 pb-7 pt-[18px]">
        <Card className="overflow-hidden p-0">
          <div className="overflow-x-auto">
            <table className="w-full border-collapse text-sm">
              <thead>
                <tr className="text-left text-xs text-muted">
                  <th className="px-5 py-3 font-medium">{t('col.name')}</th>
                  <th className="px-2 py-3 font-medium">{t('col.type')}</th>
                  <th className="px-2 py-3 font-medium">{t('col.client')}</th>
                  <th className="px-2 py-3 font-medium">{t('col.address')}</th>
                  <th className="px-5 py-3" />
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan={5} className="px-5 py-6 text-muted">{t('common.loading')}</td></tr>
                ) : rows.length === 0 ? (
                  <tr><td colSpan={5} className="px-5 py-6 text-muted">{t('common.noData')}</td></tr>
                ) : (
                  rows.map((r) => (
                    <tr key={r.id} className="cursor-pointer border-t border-divider hover:bg-bg" onClick={() => nav(`/properties/${r.id}`)}>
                      <td className="px-5 py-3.5 font-medium">{r.name}</td>
                      <td className="px-2 py-3.5"><StatusPill tone="open">{t('propType.' + r.property_type)}</StatusPill></td>
                      <td className="px-2 py-3.5 text-muted">{r.clients?.name ?? '—'}</td>
                      <td className="px-2 py-3.5 text-muted">{r.address ?? '—'}</td>
                      <td className="px-5 py-3.5 text-right">
                        <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); openEdit(r); }}>{t('form.edit')}</Button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </Card>
      </div>
      <PropertyForm open={formOpen} onClose={() => setFormOpen(false)} onSaved={load} initial={editing} />
    </Layout>
  );
}
